import { DialogComponent } from '@theme/dialog';

/**
 * A promotional popup dialog.
 *
 * Liquid only renders this element when the popup is enabled and the current
 * visitor/page match the merchant's targeting settings (home page only, guests
 * only). This component is responsible for the remaining, client-only rules:
 * showing the dialog after a configurable delay, and not showing it again until
 * a configurable number of days have passed since it was last closed.
 *
 * @extends DialogComponent
 */
export class VanityPopupComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  static #STORAGE_KEY = 'vanity-popup-last-shown';
  static #DESIGN_MODE_CLOSED_KEY = 'vanity-popup-design-mode-closed';

  /** @type {number | undefined} */
  #timeoutId;

  connectedCallback() {
    super.connectedCallback();

    // Always show in the theme editor so merchants can preview/edit the popup
    // without waiting for the delay or fighting the frequency cooldown. The
    // editor re-renders the section (and reconnects this element) on every
    // settings change, so track dismissal in sessionStorage — otherwise the
    // popup would pop back open right after being closed.
    if (this.designMode) {
      if (!this.#wasClosedInDesignMode()) {
        this.showDialog();
      }
      return;
    }

    if (!this.#isEligible()) return;

    this.#timeoutId = window.setTimeout(() => {
      this.showDialog();
    }, this.showDelay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timeoutId);
  }

  // `DialogComponent#closeDialog` is a bound instance field, not a prototype
  // method (it needs to be usable as a detached callback elsewhere), so it
  // isn't reachable through `super.closeDialog()`. Capture it here, before
  // the field below overwrites `this.closeDialog` with our own override.
  #closeDialog = this.closeDialog;

  /**
   * Closes the dialog and records when it was closed, so the frequency cooldown
   * is measured from the visitor's last interaction with the popup.
   */
  closeDialog = async () => {
    try {
      if (this.designMode) {
        window.sessionStorage.setItem(VanityPopupComponent.#DESIGN_MODE_CLOSED_KEY, 'true');
      } else {
        window.localStorage.setItem(VanityPopupComponent.#STORAGE_KEY, String(Date.now()));
      }
    } catch {
      // Storage may be unavailable (e.g. private browsing); fail silently.
    }

    await this.#closeDialog();
  };

  /**
   * @returns {boolean} Whether the popup was already dismissed in the theme
   *   editor during this browser session.
   */
  #wasClosedInDesignMode() {
    try {
      return window.sessionStorage.getItem(VanityPopupComponent.#DESIGN_MODE_CLOSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * @returns {boolean} Whether enough time has passed since the popup was last shown.
   */
  #isEligible() {
    let lastShown = 0;

    try {
      lastShown = Number(window.localStorage.getItem(VanityPopupComponent.#STORAGE_KEY)) || 0;
    } catch {
      return true;
    }

    if (!lastShown) return true;

    const frequencyMs = this.frequencyDays * 24 * 60 * 60 * 1000;
    return Date.now() - lastShown >= frequencyMs;
  }

  /** @returns {number} Seconds to wait before showing the popup. */
  get showDelay() {
    return Number(this.dataset.showDelay) || 0;
  }

  /** @returns {number} Days to wait before showing the popup again after it's closed. */
  get frequencyDays() {
    return Number(this.dataset.frequencyDays) || 0;
  }

  /** @returns {boolean} Whether the theme editor is currently active. */
  get designMode() {
    return this.dataset.designMode === 'true';
  }
}

if (!customElements.get('vanity-popup-component')) {
  customElements.define('vanity-popup-component', VanityPopupComponent);
}
